/**
 * Handles pan mode toggling and zoom filtering (Shift or explicit button).
 * Keeps zoom gestures disabled unless pan is active to prevent accidental zooms.
 * Author: bb
 */

import { useCallback, useEffect, useState } from "react";
import type { UsePanModeResult } from "./RegionalFilter.types";

/**
 * Keeps pan mode in sync with keyboard state and filters zoom gestures accordingly.
 * Shift toggles temporary pan; explicit button toggles persistent pan. Zoom events
 * are permitted only when either is active.
 */
export function usePanMode(activeState: string | null): UsePanModeResult {
  const [panModeEnabled, setPanModeEnabled] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const panModeActive = panModeEnabled || isShiftPressed;

  // Track Shift key presses so users can temporarily enter pan mode.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        setIsShiftPressed(true);
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        setIsShiftPressed(false);
      }
    };
    const handleBlur = () => setIsShiftPressed(false);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  // Disable explicit pan button when showing the whole country.
  useEffect(() => {
    if (activeState) return;
    setPanModeEnabled(false);
  }, [activeState]);

  // Allow zoom events only when pan mode is active or Shift is held.
  const handleZoomFilter = useCallback(
    (event: any) => {
      if (panModeEnabled || isShiftPressed) return true;
      const originalEvent = event?.sourceEvent ?? event;
      if (originalEvent?.shiftKey) return true;
      return false;
    },
    [isShiftPressed, panModeEnabled]
  );

  return {
    panModeEnabled,
    panModeActive,
    setPanModeEnabled,
    handleZoomFilter,
  };
}
